import { useState } from "react";
import { AffiliateOperation } from "../../interfaces/affiliate";
import { validAffiliateNickname } from "./affiliateIdentity";

export type AffiliateAdminRun = (
  operation: AffiliateOperation,
  payload: Record<string, unknown>
) => Promise<boolean>;

export function AffiliateNicknameEditor({
  address,
  nickname,
  disabled,
  run,
}: {
  address: string;
  nickname: string;
  disabled: boolean;
  run?: AffiliateAdminRun;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(nickname);
  const [accepted, setAccepted] = useState<string>();
  const confirmed = accepted
    ? "Nickname saved in backend state."
    : "Nickname cleared in backend state.";
  const save = async (value: string) => {
    if (disabled || !run || !validAffiliateNickname(value)) return;
    if (
      await run("set_affiliate_nickname", {
        affiliate: address,
        nickname: value.trim(),
      })
    ) {
      setAccepted(value.trim());
      setEditing(false);
    }
  };
  return (
    <div>
      {editing ? (
        <form
          aria-label={`Edit nickname for ${address}`}
          onSubmit={(event) => {
            event.preventDefault();
            save(draft);
          }}
        >
          <label htmlFor={`nickname-${address}`}>
            Staff nickname
            <input
              id={`nickname-${address}`}
              value={draft}
              disabled={disabled}
              onChange={(event) => setDraft(event.target.value)}
              aria-describedby={`nickname-help-${address}`}
            />
          </label>
          <small id={`nickname-help-${address}`}>
            Staff only. Up to 64 characters on one line; leave empty to clear.
          </small>
          {!validAffiliateNickname(draft) && (
            <p role="alert">
              Use up to 64 characters without line breaks or control characters.
            </p>
          )}
          <button
            type="submit"
            disabled={disabled || !validAffiliateNickname(draft)}
          >
            Save nickname
          </button>
          {nickname && (
            <button type="button" disabled={disabled} onClick={() => save("")}>
              Clear nickname
            </button>
          )}
          <button type="button" onClick={() => setEditing(false)}>
            Cancel nickname edit
          </button>
        </form>
      ) : (
        <button
          type="button"
          disabled={disabled || !run}
          title={
            disabled
              ? "Nickname editing requires an available, verified backend with nickname support."
              : undefined
          }
          aria-label={`Edit nickname for ${address}`}
          onClick={() => {
            setDraft(nickname);
            setAccepted(undefined);
            setEditing(true);
          }}
        >
          {nickname ? "Edit nickname" : "Add nickname"}
        </button>
      )}
      {accepted !== undefined && (
        <p role="status">
          {nickname === accepted
            ? confirmed
            : "Nickname command accepted; awaiting refreshed backend state."}
        </p>
      )}
    </div>
  );
}
